import {
  Box,
  Divider,
  Grid2 as Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import CompensationChart from 'components/CompensationChart'
import React, { useMemo } from 'react'
import type { UserRewardReportExpanded } from 'types/rewards'
import { formatCurrency } from 'utils/currency'

const monoFontFamily = '"Roboto Mono", "Courier New", monospace'

interface EmployeeRewardsContentProps {
  report: UserRewardReportExpanded | null
  userName: string
  userId: string
  periodLabel: string
  animated?: boolean
  headerActions?: React.ReactNode
  contentRef?: React.RefObject<HTMLDivElement | null>
  description?: string | null
  disclaimer?: string | null
  logoUrl?: string | null
}

const EmployeeRewardsContent: React.FC<EmployeeRewardsContentProps> = ({
  report,
  userName,
  userId,
  periodLabel,
  animated = true,
  headerActions,
  contentRef,
  description,
  disclaimer,
  logoUrl,
}) => {
  const theme = useTheme()

  // Extract currency settings
  const currency = report?.currencySettings?.currency || 'USD'
  const decimals = report?.currencySettings?.currencyDecimals ?? 0

  // Get compensation category and other rewards
  const { compensation, otherRewards } = useMemo(() => {
    if (!report?.categories) {
      return { compensation: 0, otherRewards: 0 }
    }

    // Find category named "Compensation"
    const compCat = report.categories.find((c) => c.categoryName === 'Compensation')
    // All other categories
    const otherCats = report.categories.filter((c) => c.categoryName !== 'Compensation')

    const compTotal = compCat?.totalCompanyContribution || 0
    const otherTotal = otherCats.reduce((sum, c) => sum + c.totalCompanyContribution, 0)

    return {
      compensation: compTotal,
      otherRewards: otherTotal,
    }
  }, [report?.categories])

  // Filter out categories with zero contributions, sort by displayOrder
  const filteredCategories = useMemo(() => {
    if (!report?.categories) return []
    return report.categories
      .filter((c) => c.totalCompanyContribution > 0 || c.totalEmployeeContribution > 0)
      .sort((a, b) => {
        const orderA = a.categoryDisplayOrder ?? Number.MAX_SAFE_INTEGER
        const orderB = b.categoryDisplayOrder ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
      .map((c) => ({
        ...c,
        rewards: [...c.rewards].sort((a, b) => {
          const orderA = a.rewardDisplayOrder ?? Number.MAX_SAFE_INTEGER
          const orderB = b.rewardDisplayOrder ?? Number.MAX_SAFE_INTEGER
          return orderA - orderB
        }),
      }))
  }, [report?.categories])

  // Calculate totals
  const totalCompanyContribution = report?.totalCompanyContribution || 0
  const totalEmployeeContribution = report?.totalEmployeeContribution || 0
  const totalRewards = totalCompanyContribution

  return (
    <div style={{ fontFamily: monoFontFamily, position: 'relative' }}>
      {/* Header actions positioned absolutely - excluded from PDF */}
      {headerActions && <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>{headerActions}</Box>}

      {/* Printable content */}
      <div ref={contentRef}>
        {/* Company Logo and Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          {/* Title and subtitle */}
          <Box sx={{ pr: headerActions ? 20 : 0 }}>
            <Typography variant="h4" fontWeight="bold">
              {userName}'s Total Rewards
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {periodLabel}
              {report?.externalId ? ` | ID: ${report.externalId}` : ''}
            </Typography>
            {description && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  mt: 1,
                  whiteSpace: 'pre-line',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {description}
              </Typography>
            )}
          </Box>

          {/* Company Logo */}
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt="Company Logo"
              sx={{
                height: 100,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>

        {/* Summary Cards Row - 3 tiles */}
        <Grid container spacing={2} mb={3}>
          {/* Compensation Card (Category 0) */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Compensation
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(compensation, currency, decimals)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {periodLabel}
              </Typography>
            </Paper>
          </Grid>

          {/* Other Rewards Card */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Other Rewards
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {formatCurrency(otherRewards, currency, decimals)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Employer contribution
              </Typography>
            </Paper>
          </Grid>

          {/* Total Rewards Card */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 3, height: '100%', bgcolor: theme.palette.primary.main }}>
              <Typography variant="subtitle2" sx={{ color: 'white', opacity: 0.9 }} gutterBottom>
                Total Rewards
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                {formatCurrency(totalRewards, currency, decimals)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                Compensation + Other Rewards
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Rewards Breakdown Table and Summary */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper>
              <TableContainer>
                <Table stickyHeader size="small" aria-label="rewards-table">
                  <TableHead
                    sx={{
                      '& th': {
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderBottom: `2px solid ${theme.palette.divider} !important`,
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ minWidth: 200, position: 'sticky !important' }}>
                        <strong>Reward</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 120, position: 'sticky !important' }}>
                        <strong>Company</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 120, position: 'sticky !important' }}>
                        <strong>Employee</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((category) => (
                        <React.Fragment key={category.categoryId}>
                          {/* Category Header Row */}
                          <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                            <TableCell>
                              <Typography fontWeight="bold">{category.categoryName}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">
                                {category.totalCompanyContribution > 0
                                  ? formatCurrency(category.totalCompanyContribution, currency, decimals)
                                  : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">
                                {category.totalEmployeeContribution > 0
                                  ? formatCurrency(category.totalEmployeeContribution, currency, decimals)
                                  : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {/* Individual Reward Rows */}
                          {category.rewards.map((reward) => (
                            <TableRow key={reward.rewardId}>
                              <TableCell sx={{ pl: 4 }}>{reward.rewardName || 'Unknown Reward'}</TableCell>
                              <TableCell align="right">
                                {reward.companyContribution > 0
                                  ? formatCurrency(reward.companyContribution, currency, decimals)
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {reward.employeeContribution > 0
                                  ? formatCurrency(reward.employeeContribution, currency, decimals)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">No rewards data available for this period</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  {filteredCategories.length > 0 && (
                    <TableFooter
                      sx={{
                        '& td': {
                          borderTop: `2px solid ${theme.palette.divider}`,
                          fontSize: 'inherit',
                          color: 'inherit',
                          backgroundColor: theme.palette.background.default,
                        },
                      }}
                    >
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Total Rewards</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {totalCompanyContribution > 0
                              ? formatCurrency(totalCompanyContribution, currency, decimals)
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {totalEmployeeContribution > 0
                              ? formatCurrency(totalEmployeeContribution, currency, decimals)
                              : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Reward Summary Sidebar with Donut Chart */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Donut Chart - show all categories */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                Rewards Breakdown
              </Typography>
              <Box sx={{ width: '100%' }}>
                <CompensationChart
                  customSeries={filteredCategories.map((c) => c.totalCompanyContribution)}
                  customLabels={filteredCategories.map((c) => c.categoryName)}
                  currency={currency}
                  currencyDecimals={decimals}
                  height={350}
                  animated={animated}
                  legendPosition="bottom"
                  verticalLegend={true}
                />
              </Box>
            </Paper>

            {/* Summary - show each category */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Reward Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {filteredCategories.map((category) => (
                <Box key={category.categoryId} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography>{category.categoryName}</Typography>
                  <Typography fontWeight="medium">
                    {formatCurrency(category.totalCompanyContribution, currency, decimals)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography fontWeight="bold">Total Rewards</Typography>
                <Typography fontWeight="bold" color="primary">
                  {formatCurrency(totalRewards, currency, decimals)}
                </Typography>
              </Box>

              {totalEmployeeContribution > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography>Your Contributions</Typography>
                    <Typography fontWeight="medium">
                      {formatCurrency(totalEmployeeContribution, currency, decimals)}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Client Disclaimer */}
        {disclaimer && (
          <Box sx={{ mt: 3, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-line', fontStyle: 'italic' }}>
              {disclaimer}
            </Typography>
          </Box>
        )}

        {/* Platform Disclaimer */}
        <Box sx={{ mt: 2, p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
            This total rewards statement is provided for informational purposes only and does not constitute a contract
            or guarantee of employment. The values shown represent estimated benefits and may be subject to change.
            Please refer to your official benefits documentation for complete details. Generated by Purcent.
          </Typography>
        </Box>
      </div>
    </div>
  )
}

export default EmployeeRewardsContent
